import axios, { AxiosInstance } from "axios";
import { EditorTree, TreeListItem } from "../../../types/types";

/**
 * Real API implementation for tree service
 */
class RealTreeService {
  private api: AxiosInstance;
  private getToken: () => string | null;

  constructor(baseURL: string, getToken: () => string | null) {
    this.getToken = getToken;
    this.api = axios.create({
      baseURL: baseURL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.api.interceptors.request.use((config) => {
      const token = this.getToken();
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    });
  }

  /**
   * Get list of all trees for the current user
   */
  async fetchTreeList(): Promise<TreeListItem[]> {
    const response = await this.api.get("/trees/");
    return response.data.map((tree: any) => ({
      id: tree.id,
      filename: tree.filename || tree.title || "untitled_tree",
      updatedAt: tree.updated_at,
      createdAt: tree.created_at,
      canDelete: tree.can_delete !== undefined ? tree.can_delete : true,
    }));
  }

  /**
   * Get a specific tree by ID
   */
  async fetchTree(id: string): Promise<EditorTree> {
    const response = await this.api.get(`/trees/${id}`);
    const tree = response.data;

    return tree;
  }

  /**
   * Create or save a tree
   */
  async saveTree(tree: EditorTree): Promise<EditorTree> {
    let response;

    // Update existing tree
    if (tree.id && tree.id !== "new") {
      response = await this.api.put(`/trees/${tree.id}`, {
        id: tree.id,
        title: tree.title,
        description: tree.description,
        created_at: tree.createdAt,
        updated_at: tree.updatedAt,
        nodes: tree.nodes,
        total_expected_value: tree.total_expected_value,
      });
    }
    // Create new tree
    else {
      response = await this.api.post("/trees/", {
        title: tree.title,
        description: tree.description,
        nodes: tree.nodes || [],
        total_expected_value: tree.total_expected_value || 0,
      });
    }

    const savedTree = response.data;

    return {
      id: savedTree.id,
      filename: savedTree.filename,
      title: savedTree.title,
      description: savedTree.description,
      createdAt: savedTree.created_at,
      updatedAt: savedTree.updated_at,
      total_expected_value: savedTree.total_expected_value || 0,
      nodes: savedTree.nodes || [],
    };
  }

  /**
   * Delete a tree
   */
  async deleteTree(id: string): Promise<void> {
    await this.api.delete(`/trees/${id}`);
  }
}

export default RealTreeService;
